import { forwardRef, useEffect, useId, useRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2, X } from "lucide-react";
import type { OrderStatus, PaymentStatus } from "@tarawa/shared";
import { cn } from "@tarawa/ui";
import { deliveryLabel, PAYMENT_LABEL } from "@/lib/driver";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger";

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "md" | "lg"; loading?: boolean }
>(({ variant = "primary", size = "md", loading, className, children, disabled, ...props }, ref) => (
  <button
    ref={ref}
    disabled={disabled || loading}
    className={cn(
      "inline-flex items-center justify-center gap-2 rounded-2xl font-semibold transition active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
      size === "lg" ? "h-14 px-6 text-base" : "h-11 px-4 text-sm",
      variant === "primary" && "bg-brand-600 text-white hover:bg-brand-700",
      variant === "secondary" && "bg-brand-50 text-brand-800 hover:bg-brand-100",
      variant === "outline" && "border border-line bg-surface hover:bg-canvas",
      variant === "ghost" && "hover:bg-black/5",
      variant === "danger" && "bg-terracotta-50 text-terracotta-600 hover:bg-terracotta-100",
      className,
    )}
    {...props}
  >
    {loading && <Loader2 className="size-4 animate-spin" />}
    {children}
  </button>
));
Button.displayName = "Button";

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)]", className)}>{children}</div>;
}

export function Loading() {
  return (
    <div className="flex justify-center py-12" role="status" aria-label="Chargement">
      <Loader2 className="size-7 animate-spin text-brand-600" />
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="rounded-2xl bg-terracotta-50 px-4 py-3 text-sm font-medium text-terracotta-600">
      {message}
      {onRetry && (
        <button onClick={onRetry} className="ms-2 underline">
          Réessayer
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-2xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">{children}</p>;
}

const DELIVERY_TONE: Partial<Record<OrderStatus, string>> = {
  CONFIRMED: "bg-[#eef4fb] text-[#2b5f9e]",
  PREPARING: "bg-[#eef4fb] text-[#2b5f9e]",
  OUT_FOR_DELIVERY: "bg-brand-50 text-brand-700",
  DELIVERED: "bg-brand-600 text-white",
  DELIVERY_FAILED: "bg-terracotta-50 text-terracotta-600",
  CANCELLED: "bg-canvas text-muted",
};

export function DeliveryBadge({ status, assigned = true }: { status: OrderStatus; assigned?: boolean }) {
  return (
    <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-bold", DELIVERY_TONE[status] ?? "bg-canvas text-muted")}>
      {assigned ? deliveryLabel(status) : "Confirmée"}
    </span>
  );
}

export function PaymentBadge({ status }: { status: PaymentStatus }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-bold",
        status === "PAID" ? "bg-brand-50 text-brand-700" : status === "PENDING" ? "bg-saffron-50 text-saffron-600" : "bg-canvas text-muted",
      )}
    >
      {PAYMENT_LABEL[status]}
    </span>
  );
}

/** Accessible modal built on the native <dialog> element (bottom sheet on phones). */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal?.();
    if (!open && d.open) d.close?.();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby={titleId}
      className="mx-auto mt-auto mb-0 w-full max-w-md rounded-t-[28px] bg-surface p-0 text-ink shadow-2xl backdrop:bg-ink/45 sm:m-auto sm:rounded-[28px]"
    >
      {open && (
        <div className="p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          <div className="mb-4 flex items-center justify-between">
            <h2 id={titleId} className="text-lg font-bold">
              {title}
            </h2>
            <button onClick={onClose} className="flex size-9 items-center justify-center rounded-full hover:bg-canvas" aria-label="Fermer">
              <X className="size-4" />
            </button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}
